import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {AlertCircle, Building, Calendar, CheckCircle, ChevronLeft, ChevronRight, Copy, CreditCard, Download, Eye, FileText, Filter, Link as LinkIcon, Mail, Plus, QrCode, Search, Settings, User, X} from "lucide-react";
import {formatDateBR, formatMoney, getBoletoStatusPalette} from "../financeiroUtils";

function CreateBoletoDialog({open, saving, receivables, bankAccounts, companies, onClose, onSubmit}) {
    const [values, setValues] = useState({contaReceberId: '', contaBancariaId: '', vencimento: ''});
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        if (!open) return;
        setValues({contaReceberId: '', contaBancariaId: '', vencimento: ''});
        setErrors({});
    }, [open]);

    const handleSubmit = async () => {
        const nextErrors = {};
        if (!values.contaReceberId) nextErrors.contaReceberId = 'Selecione uma conta a receber';
        if (!values.contaBancariaId) nextErrors.contaBancariaId = 'Selecione uma conta bancária';
        if (!values.vencimento) nextErrors.vencimento = 'Informe o vencimento';
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;

        const success = await onSubmit?.(values);
        if (success) onClose?.();
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Gerar boleto bancário</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Alert severity="info">
                        O boleto será vinculado à conta a receber selecionada e seguirá a parametrização da conta bancária.
                    </Alert>
                    <TextField
                        select
                        label="Conta a receber"
                        value={values.contaReceberId}
                        onChange={(event) => setValues((current) => ({...current, contaReceberId: event.target.value}))}
                        error={Boolean(errors.contaReceberId)}
                        helperText={errors.contaReceberId}
                    >
                        {receivables
                            .filter((item) => ['ABERTA', 'AGUARDANDO_PAGAMENTO', 'VENCIDA'].includes(item.status))
                            .map((item) => {
                                const company = companies.find((companyItem) => companyItem.id === item.empresaId);
                                return (
                                    <MenuItem key={item.id} value={item.id}>
                                        {company?.nome_fantasia || company?.razao_social || company?.nome || 'Empresa'} • {item.descricao} • {formatMoney(item.valorTotal)}
                                    </MenuItem>
                                );
                            })}
                    </TextField>
                    <TextField
                        select
                        label="Conta bancária"
                        value={values.contaBancariaId}
                        onChange={(event) => setValues((current) => ({...current, contaBancariaId: event.target.value}))}
                        error={Boolean(errors.contaBancariaId)}
                        helperText={errors.contaBancariaId}
                    >
                        {bankAccounts
                            .filter((item) => item.status === 'ATIVA')
                            .map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.banco} • {item.apelido} • Ag {item.agencia} / Cc {item.conta}
                                </MenuItem>
                            ))}
                    </TextField>
                    <TextField
                        type="date"
                        label="Vencimento"
                        value={values.vencimento}
                        onChange={(event) => setValues((current) => ({...current, vencimento: event.target.value}))}
                        error={Boolean(errors.vencimento)}
                        helperText={errors.vencimento}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                    Gerar boleto
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BoletoDetailsDialog({open, boleto, history, companies, bankAccounts, receivables, saving, onClose, onDownloadPdf, onResendEmail}) {
    const [copied, setCopied] = useState('');

    const company = companies.find((item) => item.id === boleto?.empresaId);
    const bankAccount = bankAccounts.find((item) => item.id === boleto?.contaBancariaId);
    const receivable = receivables.find((item) => item.id === boleto?.contaReceberId);
    const palette = getBoletoStatusPalette(boleto?.status);

    const copyValue = async (value, key) => {
        if (!value) return;
        await navigator.clipboard.writeText(value);
        setCopied(key);
        window.setTimeout(() => setCopied(''), 1500);
    };

    const getStatusIcon = (status) => {
        if (status === 'PAGO') return <CheckCircle size={20} color="#16a34a" />;
        if (status === 'VENCIDO') return <AlertCircle size={20} color="#dc2626" />;
        return <FileText size={20} color="#2563eb" />;
    };

    const footerAction = (
        <DialogActions sx={{position: 'sticky', bottom: 0, px: 3, py: 2, bgcolor: '#f9fafb', borderTop: '1px solid #e5e7eb', justifyContent: 'space-between'}}>
            <Typography variant="body2" color="text.secondary">
                Emitido em: {boleto?.criadoEm ? new Date(boleto.criadoEm).toLocaleString('pt-BR') : '-'}
            </Typography>
            <Stack direction="row" spacing={1.5}>
                <Button onClick={onClose} sx={{border: '1px solid #d1d5db', color: '#374151'}}>
                    Fechar
                </Button>
                <Button onClick={onResendEmail} disabled={saving} startIcon={<Mail size={16} />}>
                    Reenviar e-mail
                </Button>
                <Button variant="contained" onClick={onDownloadPdf} disabled={saving} startIcon={<Download size={16} />} sx={{bgcolor: '#2563eb', '&:hover': {bgcolor: '#1d4ed8'}}}>
                    Baixar PDF
                </Button>
            </Stack>
        </DialogActions>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{sx: {borderRadius: 3, maxHeight: '95vh'}}}>
            <DialogTitle sx={{p: 0}}>
                <Box sx={{position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1, borderBottom: '1px solid #e5e7eb'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3}}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{width: 48, height: 48, borderRadius: 2.5, bgcolor: '#ccfbf1', display: 'grid', placeItems: 'center', color: '#0f766e'}}>
                                <CreditCard size={24} />
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: '1.5rem', color: '#111827', fontWeight: 700}}>Detalhes do Boleto</Typography>
                                <Typography variant="body2" color="text.secondary">Nosso Número: {boleto?.nossoNumero || '-'}</Typography>
                            </Box>
                        </Stack>
                        <Button onClick={onClose} sx={{minWidth: 0, p: 1, borderRadius: 2, color: '#6b7280'}}>
                            <X size={18} />
                        </Button>
                    </Box>

                    {boleto ? (
                        <Box sx={{px: 3, py: 2, borderTop: '1px solid #e5e7eb', bgcolor: palette.bg, color: palette.color}}>
                            <Stack direction={{xs: 'column', md: 'row'}} alignItems={{xs: 'flex-start', md: 'center'}} justifyContent="space-between" spacing={2}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    {getStatusIcon(boleto.status)}
                                    <Box>
                                        <Typography sx={{fontWeight: 700}}>Status: {boleto.status}</Typography>
                                        {boleto.dataPagamento ? (
                                            <Typography variant="caption" sx={{opacity: 0.85}}>
                                                Pago em: {formatDateBR(boleto.dataPagamento)}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Stack>
                                <Box sx={{textAlign: {xs: 'left', md: 'right'}}}>
                                    <Typography sx={{fontSize: '1.5rem', fontWeight: 700}}>{formatMoney(boleto.valor)}</Typography>
                                    <Typography variant="caption" sx={{opacity: 0.85}}>
                                        Vencimento: {formatDateBR(boleto.dataVencimento)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    ) : null}
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{p: 3}}>
                {!boleto ? null : (
                    <Stack spacing={3}>
                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
                            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f9fafb'}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                    <Building size={18} />
                                    <Typography sx={{fontWeight: 600}}>Beneficiário</Typography>
                                </Stack>
                                <Stack spacing={1}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Empresa</Typography>
                                        <Typography sx={{color: '#111827'}}>BusiFarm Software LTDA</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                                        <Typography sx={{color: '#111827'}}>12.345.678/0001-90</Typography>
                                    </Box>
                                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5}}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Banco</Typography>
                                            <Typography sx={{color: '#111827'}}>{bankAccount?.banco || '-'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Agência/Conta</Typography>
                                            <Typography sx={{color: '#111827'}}>{bankAccount?.agencia || '-'}/{bankAccount?.conta || '-'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5}}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Convênio</Typography>
                                            <Typography sx={{color: '#111827'}}>{bankAccount?.convenio || '-'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Carteira</Typography>
                                            <Typography sx={{color: '#111827'}}>{bankAccount?.carteira || '-'}</Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f9fafb'}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                    <User size={18} />
                                    <Typography sx={{fontWeight: 600}}>Pagador</Typography>
                                </Stack>
                                <Stack spacing={1}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Nome/Razão Social</Typography>
                                        <Typography sx={{color: '#111827'}}>{company?.nome_fantasia || company?.razao_social || company?.nome || '-'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                                        <Typography sx={{color: '#111827'}}>{company?.cnpj || '-'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Endereço</Typography>
                                        <Typography sx={{color: '#111827', fontSize: 14}}>
                                            Rua Exemplo, 123 - Centro
                                            <br />
                                            São Paulo - SP - 01234-567
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>

                        {receivable ? (
                            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#eff6ff', borderColor: '#bfdbfe'}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#1d4ed8'}}>
                                    <LinkIcon size={18} />
                                    <Typography sx={{fontWeight: 600}}>Conta a Receber Vinculada</Typography>
                                </Stack>
                                <Stack spacing={1}>
                                    <Typography variant="body2" sx={{color: '#2563eb'}}>Descrição: <Typography component="span" sx={{color: '#1e3a8a'}}>{receivable.descricao}</Typography></Typography>
                                    <Typography variant="body2" sx={{color: '#2563eb'}}>Competência: <Typography component="span" sx={{color: '#1e3a8a'}}>{receivable.competencia || '-'}</Typography></Typography>
                                    <Typography variant="body2" sx={{color: '#2563eb'}}>Status: <Typography component="span" sx={{color: '#1e3a8a'}}>{receivable.status}</Typography></Typography>
                                </Stack>
                            </Paper>
                        ) : null}

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
                            <Stack spacing={2}>
                                <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f9fafb'}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                        <Calendar size={18} />
                                        <Typography sx={{fontWeight: 600}}>Informações do Boleto</Typography>
                                    </Stack>
                                    <Stack spacing={1.25}>
                                        <Typography variant="body2" color="text.secondary">Nosso Número: <Typography component="span" sx={{color: '#111827', fontFamily: 'monospace'}}>{boleto.nossoNumero || '-'}</Typography></Typography>
                                        <Typography variant="body2" color="text.secondary">Valor Original: <Typography component="span" sx={{color: '#111827'}}>{formatMoney(boleto.valor)}</Typography></Typography>
                                        <Typography variant="body2" color="text.secondary">Data de Emissão: <Typography component="span" sx={{color: '#111827'}}>{formatDateBR(boleto.dataEmissao)}</Typography></Typography>
                                        <Typography variant="body2" color="text.secondary">Vencimento: <Typography component="span" sx={{color: boleto.status === 'VENCIDO' ? '#dc2626' : '#111827', fontWeight: 600}}>{formatDateBR(boleto.dataVencimento)}</Typography></Typography>
                                        {boleto.dataPagamento ? (
                                            <Typography variant="body2" color="text.secondary">Data de Pagamento: <Typography component="span" sx={{color: '#16a34a', fontWeight: 600}}>{formatDateBR(boleto.dataPagamento)}</Typography></Typography>
                                        ) : null}
                                    </Stack>
                                </Paper>

                                <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f9fafb'}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#374151'}}>
                                        <CreditCard size={18} />
                                        <Typography sx={{fontWeight: 600}}>Código de Barras</Typography>
                                    </Stack>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.75}}>Linha Digitável</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box sx={{flex: 1, px: 1.5, py: 1.25, border: '1px solid #d1d5db', borderRadius: 1.5, bgcolor: '#fff', fontFamily: 'monospace', fontSize: 14, overflowX: 'auto'}}>
                                                    {boleto.linhaDigitavel || '-'}
                                                </Box>
                                                <Button variant="outlined" onClick={() => copyValue(boleto.linhaDigitavel, 'linha')} sx={{minWidth: 48, px: 1.5}}>
                                                    {copied === 'linha' ? <CheckCircle size={16} color="#16a34a" /> : <Copy size={16} />}
                                                </Button>
                                            </Stack>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.75}}>Código de Barras</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box sx={{flex: 1, px: 1.5, py: 1.25, border: '1px solid #d1d5db', borderRadius: 1.5, bgcolor: '#fff', fontFamily: 'monospace', fontSize: 14, overflowX: 'auto'}}>
                                                    {boleto.codigoBarras || '-'}
                                                </Box>
                                                <Button variant="outlined" onClick={() => copyValue(boleto.codigoBarras, 'barras')} sx={{minWidth: 48, px: 1.5}}>
                                                    {copied === 'barras' ? <CheckCircle size={16} color="#16a34a" /> : <Copy size={16} />}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </Stack>

                                    <Box sx={{mt: 2, p: 2, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #d1d5db'}}>
                                        <Box sx={{display: 'flex', gap: '2px', alignItems: 'flex-end', justifyContent: 'center', height: 64}}>
                                            {Array.from({length: 95}).map((_, index) => (
                                                <Box key={index} sx={{width: 2, height: `${40 + ((index * 7) % 20)}px`, bgcolor: '#111827'}} />
                                            ))}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Stack>

                            <Stack spacing={2}>
                                {boleto.qrcodePix ? (
                                    <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f0fdfa', borderColor: '#99f6e4'}}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#0f766e'}}>
                                            <QrCode size={18} />
                                            <Typography sx={{fontWeight: 600}}>QR Code PIX</Typography>
                                        </Stack>
                                        <Stack spacing={1.5} alignItems="center">
                                            <Box sx={{p: 2, borderRadius: 2, bgcolor: '#fff', border: '2px solid #5eead4'}}>
                                                <Box sx={{width: 160, height: 160, borderRadius: 1.5, bgcolor: '#f3f4f6', display: 'grid', placeItems: 'center'}}>
                                                    <QrCode size={110} color="#9ca3af" />
                                                </Box>
                                            </Box>
                                            <Typography variant="caption" sx={{color: '#0f766e'}}>Escaneie para pagar via PIX</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{width: '100%'}}>
                                                <Box sx={{flex: 1, px: 1.5, py: 1.25, border: '1px solid #5eead4', borderRadius: 1.5, bgcolor: '#fff', fontFamily: 'monospace', fontSize: 12, overflowX: 'auto'}}>
                                                    {boleto.qrcodePix}
                                                </Box>
                                                <Button variant="outlined" onClick={() => copyValue(boleto.qrcodePix, 'pix')} sx={{minWidth: 48, px: 1.5, borderColor: '#5eead4', color: '#0f766e'}}>
                                                    {copied === 'pix' ? <CheckCircle size={16} color="#16a34a" /> : <Copy size={16} />}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ) : null}

                                <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#fefce8', borderColor: '#fde68a'}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: '#a16207'}}>
                                        <Settings size={18} />
                                        <Typography sx={{fontWeight: 600}}>Configurações</Typography>
                                    </Stack>
                                    <Stack spacing={1.25}>
                                        <Typography variant="body2" sx={{color: bankAccount?.protestoAutomatico ? '#a16207' : '#6b7280'}}>
                                            {bankAccount?.protestoAutomatico ? `Protesto automático em ${bankAccount?.protestoDias || 0} dias` : 'Sem protesto automático'}
                                        </Typography>
                                        <Typography variant="body2" sx={{color: bankAccount?.aceitaAposVencimento ? '#a16207' : '#6b7280'}}>
                                            {bankAccount?.aceitaAposVencimento ? `Aceita pagamento até ${bankAccount?.diasAposVencimento || 0} dias após vencimento` : 'Não aceita após vencimento'}
                                        </Typography>
                                        <Typography variant="body2" sx={{color: bankAccount?.aceitaPagamentoParcial ? '#a16207' : '#6b7280'}}>
                                            {bankAccount?.aceitaPagamentoParcial ? `Aceita pagamento parcial (mín. ${bankAccount?.valorMinimoParcial || 0}%)` : 'Não aceita pagamento parcial'}
                                        </Typography>
                                    </Stack>
                                </Paper>

                                <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                                    <Typography sx={{fontWeight: 600, color: '#111827', mb: 2}}>Histórico</Typography>
                                    {!history.length ? (
                                        <Typography color="text.secondary">Nenhum evento registrado para este boleto.</Typography>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {history.map((item) => (
                                                <Box key={item.id} sx={{display: 'flex', justifyContent: 'space-between', gap: 2, pb: 1.25, borderBottom: '1px solid #e5e7eb'}}>
                                                    <Box>
                                                        <Typography sx={{color: '#111827', fontWeight: 500}}>{item.evento}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{item.descricao}</Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">{formatDateBR(item.criadoEm)}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </Paper>
                            </Stack>
                        </Box>
                    </Stack>
                )}
            </DialogContent>
            {footerAction}
        </Dialog>
    );
}

function CancelBoletoDialog({open, saving, onClose, onConfirm}) {
    const [reason, setReason] = useState('');

    React.useEffect(() => {
        if (open) setReason('');
    }, [open]);

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>Cancelar boleto</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Motivo"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Informe o motivo do cancelamento"
                />
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={saving}>Fechar</Button>
                <Button color="error" variant="contained" onClick={() => onConfirm?.(reason)} disabled={saving || !reason.trim()}>
                    Confirmar cancelamento
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function FinanceiroBoletosTab({
    items,
    pagination,
    loading,
    saving,
    companies,
    bankAccounts,
    receivables,
    statusFilter,
    empresaFilter,
    search,
    page,
    selectedBoleto,
    selectedBoletoHistory,
    detailLoading,
    onChangeStatusFilter,
    onChangeEmpresaFilter,
    onChangeSearch,
    onChangePage,
    onOpenDetails,
    onCloseDetails,
    onCreateBoleto,
    onCancelBoleto,
    onResendEmail,
    onDownloadPdf,
}) {
    const [createOpen, setCreateOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);

    const handleDownload = async () => {
        const result = await onDownloadPdf?.(selectedBoleto);
        if (result?.url) window.open(result.url, '_blank', 'noopener,noreferrer');
    };

    const copyCodigoBarras = async (codigo) => {
        if (!codigo) return;
        await navigator.clipboard.writeText(codigo);
    };

    return (
        <Stack spacing={3}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    value={search}
                    onChange={(event) => onChangeSearch?.(event.target.value)}
                    placeholder="Buscar por empresa, nosso número ou título..."
                    fullWidth
                    InputProps={{startAdornment: <Search size={18} color="#9ca3af" style={{marginRight: 8}} />}}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                        },
                    }}
                />
                <TextField
                    select
                    value={statusFilter || 'TODOS'}
                    onChange={(event) => onChangeStatusFilter?.(event.target.value === 'TODOS' ? '' : event.target.value)}
                    sx={{
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                        },
                    }}
                >
                    <MenuItem value="TODOS">Todos</MenuItem>
                    <MenuItem value="EMITIDO">Emitidos</MenuItem>
                    <MenuItem value="REGISTRADO">Registrados</MenuItem>
                    <MenuItem value="PAGO">Pagos</MenuItem>
                    <MenuItem value="VENCIDO">Vencidos</MenuItem>
                </TextField>
                <Button
                    variant="outlined"
                    startIcon={<Filter size={18} />}
                    sx={{minWidth: 120, borderRadius: 2, color: '#374151', borderColor: '#d1d5db'}}
                >
                    Filtros
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={() => setCreateOpen(true)}
                    disabled={!bankAccounts.length}
                    sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}, whiteSpace: 'nowrap', borderRadius: 2}}
                >
                    Gerar boleto
                </Button>
            </Stack>

            {!bankAccounts.length ? (
                <Alert severity="warning" sx={{border: '1px solid #fde68a', bgcolor: '#fefce8', color: '#854d0e'}}>
                    <strong>Atenção:</strong> Nenhuma conta bancária cadastrada. Configure uma conta bancária antes de gerar boletos.
                </Alert>
            ) : null}

            <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                <Table>
                    <TableHead>
                        <TableRow sx={{bgcolor: '#f9fafb'}}>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Nosso número</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell>Conta</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items.length ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                        {loading ? 'Carregando boletos...' : 'Nenhum boleto encontrado.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : items.map((item) => {
                            const company = companies.find((companyItem) => companyItem.id === item.empresaId);
                            const bankAccount = bankAccounts.find((bankAccountItem) => bankAccountItem.id === item.contaBancariaId);
                            const palette = getBoletoStatusPalette(item.status);
                            return (
                                <TableRow key={item.id} hover sx={item.status === 'VENCIDO' ? {bgcolor: '#fef2f2'} : undefined}>
                                    <TableCell>
                                        <Typography sx={{color: '#111827', fontWeight: 500}}>{company?.nome_fantasia || company?.razao_social || company?.nome || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">{company?.cnpj || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{item.nossoNumero || '-'}</TableCell>
                                    <TableCell>{formatMoney(item.valor)}</TableCell>
                                    <TableCell>
                                        <Chip label={item.status} size="small" sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{fontSize: 14, color: item.status === 'VENCIDO' ? '#dc2626' : '#374151'}}>
                                            {formatDateBR(item.dataVencimento)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{fontSize: 14, color: '#374151'}}>{bankAccount?.banco || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{bankAccount?.agencia || '-'} / {bankAccount?.conta || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Copiar código de barras">
                                            <IconButton onClick={() => copyCodigoBarras(item.codigoBarras)} sx={{color: '#4b5563'}}>
                                                <Copy size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download PDF">
                                            <IconButton
                                                onClick={async () => {
                                                    await onOpenDetails?.(item.id);
                                                    const result = await onDownloadPdf?.(item);
                                                    if (result?.url) window.open(result.url, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={{color: '#2563eb'}}
                                            >
                                                <Download size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ver detalhes">
                                            <IconButton onClick={() => onOpenDetails?.(item.id)} sx={{color: '#0f766e'}}>
                                                <Eye size={18} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Mostrando {(Number(page || 0) * 10) + (items.length ? 1 : 0)} a {Math.min((Number(page || 0) * 10) + items.length, pagination?.total || items.length)} de {pagination?.total || items.length} boletos
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={!pagination?.has_previous} onClick={() => onChangePage?.(Math.max(0, Number(page || 0) - 1))} sx={{minWidth: 44, px: 1.5}}>
                        <ChevronLeft size={18} />
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{display: 'flex', alignItems: 'center', px: 0.5}}>
                        Página {Number(page || 0) + 1} de {pagination?.total_pages || 1}
                    </Typography>
                    <Button variant="outlined" disabled={!pagination?.has_next} onClick={() => onChangePage?.(Number(page || 0) + 1)} sx={{minWidth: 44, px: 1.5}}>
                        <ChevronRight size={18} />
                    </Button>
                </Stack>
            </Stack>

            {selectedBoleto?.status === 'VENCIDO' ? (
                <Alert severity="warning" icon={<AlertCircle size={18} />}>
                    Este boleto está vencido. Consulte a parametrização da conta bancária antes de reenviar a cobrança.
                </Alert>
            ) : null}

            <CreateBoletoDialog
                open={createOpen}
                saving={saving}
                receivables={receivables}
                bankAccounts={bankAccounts}
                companies={companies}
                onClose={() => setCreateOpen(false)}
                onSubmit={onCreateBoleto}
            />

            <BoletoDetailsDialog
                open={Boolean(selectedBoleto)}
                boleto={selectedBoleto}
                history={selectedBoletoHistory}
                companies={companies}
                bankAccounts={bankAccounts}
                receivables={receivables}
                saving={saving || detailLoading}
                onClose={onCloseDetails}
                onDownloadPdf={handleDownload}
                onResendEmail={() => onResendEmail?.(selectedBoleto?.id)}
            />

            <CancelBoletoDialog
                open={cancelOpen}
                saving={saving}
                onClose={() => {
                    setCancelOpen(false);
                    setCancelTarget(null);
                }}
                onConfirm={async (reason) => {
                    const success = await onCancelBoleto?.(cancelTarget?.id || selectedBoleto?.id, reason);
                    if (success) {
                        setCancelOpen(false);
                        setCancelTarget(null);
                        onCloseDetails?.();
                    }
                }}
            />
        </Stack>
    );
}
